import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AlertCircle, Clock, RefreshCw } from 'lucide-react';
import { API } from '../App';
import { useT } from '../i18n';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

export const HISTORY_LIMIT = 100;

// The server stores executed_at as UTC; a value without a zone must not be read as local time.
export function parseUtc(value) {
  if (!value) return null;
  const s = String(value);
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/*
  "History" tab of the Automation page: the last rule runs from
  GET /api/automation/history (automation_history, newest first).
  A load error is shown as an error with a retry, never as an empty history.
*/
export default function AutomationHistory({ campaigns = [], rules = [], ruleTypeLabel, active = true }) {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [state, setState] = useState('idle'); // idle | loading | ready | error

  const load = useCallback(async () => {
    setState('loading');
    try {
      const response = await axios.get(`${API}/automation/history`, { params: { limit: HISTORY_LIMIT } });
      setItems(Array.isArray(response.data) ? response.data : []);
      setState('ready');
    } catch (error) {
      console.error('Error fetching automation history:', error);
      setState('error');
    }
  }, []);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const campaignName = (id) => {
    if (!id) return '—';
    const c = (campaigns || []).find((x) => x.id === id);
    return c ? c.name : id;
  };
  const actionLabel = (ruleId) => {
    const r = (rules || []).find((x) => x.id === ruleId);
    if (!r) return '—';
    return ruleTypeLabel ? ruleTypeLabel(r.rule_type) : r.rule_type;
  };

  const header = (
    <div className="flex items-center justify-between">
      <div>
        <h2 className="text-xl font-semibold">{t('Execution history')}</h2>
        <p className="text-sm text-muted-foreground">{t('The last {count} rule runs, newest first.', { count: HISTORY_LIMIT })}</p>
      </div>
      <Button variant="outline" size="sm" onClick={load} disabled={state === 'loading'} data-testid="automation-history-refresh">
        <RefreshCw className={`w-4 h-4 mr-2 ${state === 'loading' ? 'animate-spin' : ''}`} />
        {t('Refresh history')}
      </Button>
    </div>
  );

  let body;
  if (state === 'error') {
    body = (
      <Card data-testid="automation-history-error">
        <CardContent className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <p className="font-medium">{t('Could not load the execution history.')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('This is a loading error, not an empty history. Try again in a minute.')}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={load}>
            {t('Try loading again')}
          </Button>
        </CardContent>
      </Card>
    );
  } else if (state !== 'ready') {
    body = (
      <Card>
        <CardContent className="text-center py-12 text-muted-foreground" data-testid="automation-history-loading">
          {t('Loading the execution history…')}
        </CardContent>
      </Card>
    );
  } else if (!items.length) {
    body = (
      <Card data-testid="automation-history-empty">
        <CardContent className="text-center py-12">
          <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t('Execution history is empty')}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {t('Records of automatic rule executions will appear here')}
          </p>
        </CardContent>
      </Card>
    );
  } else {
    body = (
      <Card>
        <CardContent className="p-0">
          <Table data-testid="automation-history-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('Run time')}</TableHead>
                <TableHead>{t('Rule name')}</TableHead>
                <TableHead>{t('Campaign name')}</TableHead>
                <TableHead>{t('Rule type')}</TableHead>
                <TableHead>{t('What the rule did')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => {
                const when = parseUtc(item.executed_at);
                return (
                  <TableRow key={item.id || `${item.rule_id}-${item.executed_at}`}>
                    <TableCell className="whitespace-nowrap text-sm">{when ? when.toLocaleString() : '—'}</TableCell>
                    <TableCell className="font-medium">{item.rule_name || '—'}</TableCell>
                    <TableCell>{campaignName(item.details && item.details.campaign_id)}</TableCell>
                    <TableCell>{actionLabel(item.rule_id)}</TableCell>
                    <TableCell className="text-sm">{item.action_taken || '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {header}
      {body}
    </div>
  );
}
